import { Head } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { PageProps } from '@/types'
import { DateRange } from '@/components/DateRangeFilter'
import DataTable, { Column } from '@/components/DataTable'
import ReportControlBar, { makeSortHandler } from '@/components/ReportControlBar'
import { Clock, CheckCircle, XCircle } from 'lucide-react'
import { ChartCard, FunnelChart, ChartColors } from '@/components/ReportCharts'

interface Row {
  id: number
  name: string
  email: string
  status: string
  referring_agent_name: string
  created_at: string
  document_sent_at: string | null
  document_signed_at: string | null
}

interface Props extends PageProps {
  rows: Row[]
  totals: { invited: number; document_signed: number; past_agent: number }
  date_range: DateRange
  sort: { key: string | null; dir: string | null }
}

const PATH = '/admin/reports/referral_funnel'

function fmt(d: string | null) { return d ? new Date(d).toLocaleDateString() : '—' }

export default function ReferralFunnel({ rows, totals, date_range, sort }: Props) {
  const onSortChange = makeSortHandler(PATH, date_range, {})
  const columns: Column<Row>[] = [
    { key: 'name', header: 'Referral', sortable: true,
      render: (r) => (
        <div>
          <div className="font-medium text-chestnut-charcoal">{r.name}</div>
          <div className="text-xs text-chestnut-charcoal/60">{r.email}</div>
        </div>
      ) },
    { key: 'status', header: 'Status', sortable: true },
    { key: 'referring_agent_name', header: 'Invited By', sortable: true },
    { key: 'created_at', header: 'Created', sortable: true, render: (r) => fmt(r.created_at) },
    { key: 'document_sent_at', header: 'Doc Sent', sortable: true, render: (r) => fmt(r.document_sent_at) },
    { key: 'document_signed_at', header: 'Doc Signed', sortable: true, render: (r) => fmt(r.document_signed_at) }
  ]

  const cards = [
    { label: 'Invited', value: totals.invited, icon: <Clock className="h-5 w-5 text-amber-700" />, bg: 'bg-amber-100' },
    { label: 'Document Signed', value: totals.document_signed, icon: <CheckCircle className="h-5 w-5 text-green-700" />, bg: 'bg-green-100' },
    { label: 'Past Agent', value: totals.past_agent, icon: <XCircle className="h-5 w-5 text-gray-700" />, bg: 'bg-gray-100' }
  ]

  return (
    <AppLayout>
      <Head title="Referral Funnel" />
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Referral Funnel</h1>
          <p className="text-chestnut-charcoal/70 mt-1">Referral status totals with the underlying referral list.</p>
        </div>

        <ReportControlBar path={PATH} dateRange={date_range} sortState={sort} />

        <ChartCard
          title="Referral funnel"
          subtitle="Conversion from invite through document signed and past-agent."
          height={200}
          empty={totals.invited === 0 && totals.document_signed === 0 && totals.past_agent === 0}
          emptyMessage="No referrals in this date range."
        >
          <FunnelChart
            steps={[
              { label: 'Invited', value: totals.invited, color: ChartColors.AMBER },
              { label: 'Document Signed', value: totals.document_signed, color: ChartColors.GREEN },
              { label: 'Past Agent', value: totals.past_agent, color: ChartColors.GRAY }
            ]}
          />
        </ChartCard>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {cards.map(c => (
            <div key={c.label} className="bg-white rounded-xl border border-chestnut-gray p-5">
              <div className="flex items-center gap-3">
                <div className={`p-2.5 rounded-lg ${c.bg}`}>{c.icon}</div>
                <div>
                  <p className="text-2xl font-bold text-chestnut-charcoal">{c.value}</p>
                  <p className="text-xs text-chestnut-charcoal/60">{c.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-chestnut-gray p-6">
          <DataTable
            data={rows}
            columns={columns}
            searchPlaceholder="Search referrals..."
            searchKeys={['name', 'email', 'referring_agent_name']}
            pageSize={20}
            emptyMessage="No referrals in this date range."
            getRowKey={(r) => r.id}
            controlledSort={{ key: sort.key ?? 'created_at', direction: (sort.dir as 'asc' | 'desc') ?? 'desc' }}
            onSortChange={onSortChange}
          />
        </div>
      </div>
    </AppLayout>
  )
}
